import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useStudent } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const EDUCATION_LEVELS = ["HSC / A-Levels or equivalent", "Undergraduate degree", "Postgraduate degree", "Diploma / Foundation", "Other"];
const INTAKES = ["2027", "2028", "Later", "Not sure yet"];

const inputCls = "rounded-none border-input bg-white px-4 py-6 text-navy placeholder:text-navy/40 focus-visible:ring-gold";

export default function LeadForm({ source = "guidance-form" }) {
  const { student } = useStudent();
  const [form, setForm] = useState({
    name: "", email: "", whatsapp: "", country: "", education_level: "",
    study_interest: "", preferred_university: "", intake: "", consent: false,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (student) {
      setForm((f) => ({
        ...f,
        name: f.name || student.name || "",
        email: f.email || student.email || "",
      }));
    }
  }, [student]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please agree to the privacy note before submitting.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, source });
      toast.success("Thank you — we'll be in touch shortly with your next steps.");
      setForm({ name: "", email: "", whatsapp: "", country: "", education_level: "", study_interest: "", preferred_university: "", intake: "", consent: false });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form data-testid="lead-form" onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-6">
      <div>
        <Label htmlFor="lf-name" className="text-xs uppercase tracking-[0.2em] text-navy/60">Name</Label>
        <Input id="lf-name" data-testid="lead-name-input" required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your full name" className={`mt-2 ${inputCls}`} />
      </div>
      <div>
        <Label htmlFor="lf-email" className="text-xs uppercase tracking-[0.2em] text-navy/60">Email</Label>
        <Input id="lf-email" data-testid="lead-email-input" required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" className={`mt-2 ${inputCls}`} />
      </div>
      <div>
        <Label htmlFor="lf-whatsapp" className="text-xs uppercase tracking-[0.2em] text-navy/60">WhatsApp number</Label>
        <Input id="lf-whatsapp" data-testid="lead-whatsapp-input" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+880 ..." className={`mt-2 ${inputCls}`} />
      </div>
      <div>
        <Label htmlFor="lf-country" className="text-xs uppercase tracking-[0.2em] text-navy/60">Current country</Label>
        <Input id="lf-country" data-testid="lead-country-input" value={form.country} onChange={(e) => set("country", e.target.value)} placeholder="e.g. Bangladesh" className={`mt-2 ${inputCls}`} />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-[0.2em] text-navy/60">Current education level</Label>
        <Select value={form.education_level} onValueChange={(v) => set("education_level", v)}>
          <SelectTrigger data-testid="lead-education-select" className={`mt-2 ${inputCls}`}>
            <SelectValue placeholder="Select level" />
          </SelectTrigger>
          <SelectContent>
            {EDUCATION_LEVELS.map((l) => <SelectItem key={l} value={l} data-testid={`lead-edu-${l.slice(0, 12).replace(/\W+/g, "-").toLowerCase()}`}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="lf-study" className="text-xs uppercase tracking-[0.2em] text-navy/60">What would you like to study?</Label>
        <Input id="lf-study" data-testid="lead-study-input" value={form.study_interest} onChange={(e) => set("study_interest", e.target.value)} placeholder="e.g. Computer Science" className={`mt-2 ${inputCls}`} />
      </div>
      <div>
        <Label htmlFor="lf-uni" className="text-xs uppercase tracking-[0.2em] text-navy/60">Preferred Australian university, if any</Label>
        <Input id="lf-uni" data-testid="lead-university-input" value={form.preferred_university} onChange={(e) => set("preferred_university", e.target.value)} placeholder="Optional" className={`mt-2 ${inputCls}`} />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-[0.2em] text-navy/60">Expected intake</Label>
        <Select value={form.intake} onValueChange={(v) => set("intake", v)}>
          <SelectTrigger data-testid="lead-intake-select" className={`mt-2 ${inputCls}`}>
            <SelectValue placeholder="Select intake" />
          </SelectTrigger>
          <SelectContent>
            {INTAKES.map((i) => <SelectItem key={i} value={i} data-testid={`lead-intake-${i.replace(/\W+/g, "-").toLowerCase()}`}>{i}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="sm:col-span-2 flex items-start gap-3">
        <Checkbox id="lf-consent" data-testid="lead-consent-checkbox" checked={form.consent} onCheckedChange={(v) => set("consent", v === true)} className="mt-0.5 rounded-none border-navy/40 data-[state=checked]:bg-navy" />
        <Label htmlFor="lf-consent" className="text-sm text-navy/70 leading-relaxed font-normal cursor-pointer">
          I agree to be contacted by Vitaliah about my enquiry and understand my information will be handled in line with the privacy policy. No spam — ever.
        </Label>
      </div>
      <div className="sm:col-span-2">
        <button
          data-testid="lead-submit-button"
          type="submit"
          disabled={loading}
          className="group inline-flex items-center gap-3 bg-navy text-cream px-10 py-4 text-sm font-semibold tracking-wide border border-navy hover:bg-gold hover:border-gold hover:text-navy transition-colors duration-300 disabled:opacity-60"
        >
          {loading ? "Sending..." : "Get Free Guidance"}
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </button>
      </div>
    </form>
  );
}
